import { useRouter } from "next/router";
import { useGroups } from "@nju/data-access-portal-dashboard";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { IOfferModeWithFSecure } from "@nju/data-access-portal-offers";
import {
  onboardingStarted,
  useOrchestrators,
} from "@nju/data-access-portal-orchestrator";
import { getFeatureFlagValue } from "@nju/feature-flags";
import { portalRoutes } from "@nju/routes";
import { Separator } from "@nju/ui";
import { Button } from "./Button";
import { resolveBadge } from "./utils";

interface IOfferTypeSwitcherProps {
  context: IOfferModeWithFSecure;
  handleSetMode: (mode: IOfferModeWithFSecure) => void;
  handleMissingFSecure: () => void;
  isLoading?: boolean;
}

export function OfferTypeSwitcher({
  context,
  handleSetMode,
  handleMissingFSecure,
  isLoading,
}: IOfferTypeSwitcherProps) {
  const isPostpaidEnabled = getFeatureFlagValue("postpaid") === "1";
  const router = useRouter();
  const params = portalRoutes.mojeKonto.index.input(router.query).unwrap();
  const userId = params?.userId;
  const flow = params?.flow;
  const { ftth, mobile, multiOrder, fsecure } = useOrchestrators();
  const memberOrchestrators = useMemberOrchestrator();
  const groups = useGroups();
  const isMissingMobileOrchestrator =
    (!mobile ||
      (mobile.offerCategory && mobile.offerCategory !== "MOBILE") ||
      !onboardingStarted(mobile.state)) &&
    (!memberOrchestrators?.mobile ||
      (memberOrchestrators.mobile.offerCategory &&
        memberOrchestrators.mobile.offerCategory !== "MOBILE") ||
      !onboardingStarted(memberOrchestrators.mobile.state));
  const isMissingMobileMultiOrderOrchestrator =
    !multiOrder ||
    (multiOrder.offerCategory && multiOrder.offerCategory !== "MOBILE") ||
    !onboardingStarted(multiOrder.state);
  const hasMobileProduct = !!groups?.some((group) => {
    return group.members?.some((member) => {
      return (
        member.primaryMsisdn &&
        (member.offerCategory === "MOBILE" || !member.offerCategory)
      );
    });
  });
  const isMissingMobile =
    isMissingMobileOrchestrator &&
    isMissingMobileMultiOrderOrchestrator &&
    !hasMobileProduct;
  const isMissingPostpaidOrchestrator =
    (!mobile ||
      mobile.offerCategory !== "MOBILE_POSTPAID" ||
      !onboardingStarted(mobile.state)) &&
    (!memberOrchestrators?.mobile ||
      memberOrchestrators.mobile.offerCategory !== "MOBILE_POSTPAID" ||
      !onboardingStarted(memberOrchestrators.mobile.state));
  const isMissingPostpaidMultiOrderOrchestrator =
    !multiOrder ||
    multiOrder.offerCategory !== "MOBILE_POSTPAID" ||
    !onboardingStarted(multiOrder.state);
  const hasPostpaidProduct = !!groups?.some((group) => {
    return group.members?.some((member) => {
      return member.primaryMsisdn && member.offerCategory === "MOBILE_POSTPAID";
    });
  });
  const isMissingPostpaid =
    isMissingPostpaidOrchestrator &&
    isMissingPostpaidMultiOrderOrchestrator &&
    !hasPostpaidProduct;
  const isMissingFsecure = !fsecure || !onboardingStarted(fsecure.state);
  const isMissingFtth = !ftth || !onboardingStarted(ftth.state);

  return (
    <>
      <div className="flex items-center h-12 gap-4 overflow-x-auto scrollbar-width-none webkit-scrollbar-none ms-overflow-style-none">
        {isPostpaidEnabled && (
          <div className="flex-shrink-0">
            <Button
              badge={undefined}
              flow={flow}
              isActive={context === "postpaid"}
              isCompleted={hasPostpaidProduct}
              isMissing={isMissingPostpaid}
              label="nju abonament"
              mode="postpaid"
              onClick={() => {
                return handleSetMode("postpaid");
              }}
              testId="mobile-switcher-button"
              userId={userId}
            />
          </div>
        )}
        <div className="flex-shrink-0">
          <Button
            badge={undefined}
            flow={flow}
            isActive={context === "mobile"}
            isCompleted={hasMobileProduct}
            isMissing={isMissingMobile}
            label="nju subskrypcja"
            mode="mobile"
            onClick={() => {
              return handleSetMode("mobile");
            }}
            testId="mobile-switcher-button"
            userId={userId}
          />
        </div>
        <div className="flex-shrink-0">
          <Button
            badge={resolveBadge(ftth)}
            flow={flow}
            isActive={context === "ftth"}
            isCompleted={ftth?.state === "COMPLETED"}
            isMissing={isMissingFtth}
            label="nju światłowód"
            mode="ftth"
            onClick={() => {
              return handleSetMode("ftth");
            }}
            testId="ftth-switcher-button"
            userId={userId}
          />
        </div>
        <div className="flex-shrink-0">
          <Button
            allowRedirect={!isMissingFsecure}
            badge={resolveBadge(fsecure)}
            flow={flow}
            isActive={context === "fsecure"}
            isCompleted={fsecure?.state === "COMPLETED"}
            isLoading={isLoading}
            isMissing={isMissingFsecure}
            label="Bezpieczny internet"
            mode="fsecure"
            onClick={() => {
              if (isMissingFsecure) {
                return handleMissingFSecure();
              }
              return handleSetMode("fsecure");
            }}
            testId="fsecure-switcher-button"
            userId={userId}
          />
        </div>
      </div>
      <Separator size="m" />
    </>
  );
}
